// components/auth/ProtectedRoute.js - Enhanced with session validation
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { sessionManager } from '../../../shared/services/sessionManagement/sessionManager';
import { useUser } from '../hooks/useUser';
import PostLoginLoader from './PostLoginLoader';

export const ProtectedRoute = () => {
  const { isAuthenticated, isHydrating } = useUser();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Wait for user data hydration before making auth decisions —
    // otherwise the deferred JSON.parse hasn't completed yet and
    // the user would incorrectly appear unauthenticated.
    if (isHydrating) return;

    const validateAccess = () => {
      // Check user authentication
      const userAuth = isAuthenticated();

      // Check session validity
      const sessionValid = sessionManager.isAuthenticated();

      const isAccessValid = !!(userAuth && sessionValid);

      setIsValid(isAccessValid);
      setIsValidating(false);

      // If not valid, session manager will handle the redirect
      if (!isAccessValid && sessionValid === false) {
        // Session expired, let sessionManager handle it
        return;
      }
    };

    validateAccess();

    // Listen for session expiry
    const handleSessionExpired = () => {
      setIsValid(false);
      setIsValidating(false);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [isAuthenticated, isHydrating, location]);

  if (isValidating) {
    return <PostLoginLoader />;
  }

  return isValid ? <Outlet /> : <Navigate to="/" state={{ from: location }} replace />;
};